import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from "react-native";
import React, { useState, useEffect } from "react";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import Fonts from "../../../Resources/Fonts";
import CustomAlert from "../../Components/CustomAlert";
import { observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "../../_layout";
import { useTranslation } from "react-i18next";
import SegmentedPicker from "../../Components/SegmentedPicker";
import { formatNumber2Dec } from "../../utils/numberUtils";

const ClientProfile = observer(({ client }) => {
  const { screenSize } = useScreenDimensions();
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
  const deleteAlertTitle = "deleteClientAlertTitle-string";
  const deleteAlertText = "deleteClientAlertText-string";

  const deleteClient = async () => {
    await client.deleteClient(); // Use await to ensure the function completes
  };

  useEffect(() => {
    client.setSettings("name_for_coach", client.nameForCoach);
    client.setSettings("surname_for_coach", client.surnameForCoach);
    client.setSettings("notes", client.notes);
    client.setSettings("phone", client.phone);
    client.setSettings("is_competitor", client.isCompetitor);
    client.setSettings("gender", client.gender);
    client.setSettings("profile_squat", client.profileSquat);
    client.setSettings("profile_bench_press", client.profileBenchPress);
    client.setSettings("profile_deadlift", client.profileDeadlift);
  }, []);

  const TextName = ({ property, firestoreProperty, placeholder }) => {
    const [keyword, setKeyword] = useState(
      client.getProperty(property) ? client.getProperty(property) : ""
    );
    const onChangeText = (text) => {
      setKeyword(text);
      client.setSettings(firestoreProperty, text);
    };

    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        style={[
          styles.textInputContainer,
          {
            backgroundColor: "transparent",
            width: screenSize === "small" ? "80%" : "45%",
            borderColor: isHovered ? "rgba(0, 0, 0, 0.5)" : Colors.borderColor,
          },
        ]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TextInput
          value={keyword}
          onChangeText={onChangeText}
          style={[
            styles.textInputStyle,
            { color: "black", outline: "none", width: "100%" },
          ]}
          placeholder={t(placeholder)}
          placeholderTextColor={Colors.grayColor}
          maxLength={40}
        />
      </View>
    );
  };

  const UnmutableTextName = ({ property, placeholder }) => {
    return (
      <View
        style={[
          styles.textInputContainer,
          {
            backgroundColor: "transparent",
            width: screenSize === "small" ? "80%" : "45%",
            // borderWidth: 5,
            // borderColor: Colors.grayColor,
          },
        ]}
      >
        <Text
          style={[
            styles.textInputStyle,
            { color: Colors.grayColor, outline: "none", width: "100%" },
          ]}
          numberOfLines={1}
        >
          {client.getProperty(property)
            ? client.getProperty(property)
            : placeholder}
        </Text>
      </View>
    );
  };

  const TextNotes = () => {
    const [string, setString] = useState(client.notes ? client.notes : "");

    const handleChange = (text) => {
      setString(text);
      client.setSettings("notes", text);
    };

    const [isHovered, setIsHovered] = useState(false);

    return (
      <View
        style={{
          marginRight: screenSize === "small" ? 0 : 60,
          marginLeft: 80,
          borderRadius: 10,
          borderWidth: 0.1,
          flex: 1,
          borderColor: isHovered ? "rgba(0, 0, 0, 0.5)" : Colors.borderColor,
          textAlignVertical: "top",
          minHeight: 80,
        }}
      >
        <TextInput
          value={string}
          onChangeText={handleChange}
          multiline
          placeholder={t("notesForCoach-string")}
          placeholderTextColor={Colors.grayColor}
          style={{
            outlineStyle: "none",
            position: "absolute",
            paddingVertical: 8,
            paddingHorizontal: 10,
            width: "100%",
            height: "100%",
            fontSize: 12,
          }}
          autoCorrect={false}
          spellCheck={false}
          maxLength={1000}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
        <View
          style={{
            position: "relative",
            zIndex: -2,
            width: "100%",
            paddingVertical: 8,
            paddingHorizontal: 10,
          }}
        >
          <Text
            style={{
              color: "transparent",
              fontSize: 12,
            }}
          >
            {string}
          </Text>
        </View>
      </View>
    );
  };

  const propertyMap = {
    squat: { value: client.profileSquat, settingsKey: "profile_squat" },
    benchPress: {
      value: client.profileBenchPress,
      settingsKey: "profile_bench_press",
    },
    deadlift: {
      value: client.profileDeadlift,
      settingsKey: "profile_deadlift",
    },
  };

  const CompetitionRegisterTextBox = ({ propertyCase }) => {
    const { value, settingsKey } = propertyMap[propertyCase] || {};

    const [propertyString, setPropertyString] = useState(
      value > 0 ? formatNumber2Dec(value) : ""
    );
    const [isHovered, setIsHovered] = useState(false);

    const handleChange = (text) => {
      const formattedText = text.replace(",", ".");
      const num = Number(formattedText);
      if (!isNaN(num)) {
        setPropertyString(formattedText);
        const numAux = formattedText === "" ? 0 : num + 1e-7;
        client.setSettings(settingsKey, numAux);
      }
    };

    return (
      <View
        style={[
          styles.competitionTextBoxContainer,
          {
            borderColor: isHovered ? "rgba(0, 0, 0, 0.5)" : Colors.borderColor,
            width: screenSize === "small" ? "80%" : "45%",
            minWidth: screenSize === "large" ? 100 : undefined,
          },
        ]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TextInput
          value={propertyString}
          onChangeText={handleChange}
          inputMode="numeric"
          style={styles.competitionTextBoxInput}
          maxLength={7}
        />
      </View>
    );
  };

  const BestCompetitionLifts = () => {
    const lifts = [
      { key: "squat", label: t("squat-string") },
      { key: "benchPress", label: t("benchPress-string") },
      { key: "deadlift", label: t("deadlift-string") },
    ];

    return (
      <View
        style={[
          styles.bestLiftsContainer,
          { flexDirection: screenSize !== "large" ? "column" : "row" },
        ]}
      >
        {lifts.map((lift, index) => (
          <View
            key={lift.key}
            style={[
              styles.liftRow,
              {
                marginBottom:
                  screenSize !== "large" && index < lifts.length - 1 ? 20 : 0,
                width: screenSize !== "large" ? "100%" : undefined,
              },
            ]}
          >
            <Text
              style={[
                Fonts.regular14,
                styles.liftLabel,
                {
                  width:
                    screenSize === "small"
                      ? 120
                      : screenSize === "medium"
                      ? 180
                      : undefined,
                },
              ]}
            >
              {lift.label}
            </Text>
            <View style={styles.liftTextBoxWrapper}>
              <CompetitionRegisterTextBox propertyCase={lift.key} />
            </View>
          </View>
        ))}
      </View>
    );
  };

  const SaveButton = observer(() => {
    const saveClientProfile = () => {
      let hasChanges = false;
      if (!client.isOffline) {
        if (client.clientSettings["name_for_coach"] !== client.nameForCoach) {
          client.setProperty(
            "nameForCoach",
            client.clientSettings["name_for_coach"]
          );
          hasChanges = true;
        }

        if (
          client.clientSettings["surname_for_coach"] !== client.surnameForCoach
        ) {
          client.setProperty(
            "surnameForCoach",
            client.clientSettings["surname_for_coach"]
          );
          hasChanges = true;
        }

        if (client.clientSettings["notes"] !== client.notes) {
          client.setProperty("notes", client.clientSettings["notes"]);
          hasChanges = true;
        }

        if (client.clientSettings["phone"] !== client.phone) {
          client.setProperty("phone", client.clientSettings["phone"]);
          hasChanges = true;
        }

        if (client.clientSettings["is_competitor"] !== client.isCompetitor) {
          client.setProperty(
            "isCompetitor",
            client.clientSettings["is_competitor"]
          );
          hasChanges = true;
        }

        if (client.clientSettings["gender"] !== client.gender) {
          client.setProperty("gender", client.clientSettings["gender"]);
          hasChanges = true;
        }

        if (client.clientSettings["profile_squat"] !== client.profileSquat) {
          client.setProperty(
            "profileSquat",
            client.clientSettings["profile_squat"]
          );
          hasChanges = true;
        }

        if (
          client.clientSettings["profile_bench_press"] !==
          client.profileBenchPress
        ) {
          client.setProperty(
            "profileBenchPress",
            client.clientSettings["profile_bench_press"]
          );
          hasChanges = true;
        }

        if (
          client.clientSettings["profile_deadlift"] !== client.profileDeadlift
        ) {
          client.setProperty(
            "profileDeadlift",
            client.clientSettings["profile_deadlift"]
          );
          hasChanges = true;
        }

        if (hasChanges) {
          client.saveClientProfile(client.clientSettings);
        }
      }
    };
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        onPress={saveClientProfile}
        style={{
          height: 40,
          minWidth: 80,
          borderRadius: 14,
          backgroundColor: Colors.bubbleColor,
          borderWidth: 0.1,
          borderColor: "rgba(0, 0, 0,0.2)",
          paddingHorizontal: 10,
          alignItems: "center",
          justifyContent: "space-around",
          opacity: client.isOffline
            ? 0.5
            : client.clientSettings["name_for_coach"] !== client.nameForCoach ||
              client.clientSettings["surname_for_coach"] !==
                client.surnameForCoach ||
              client.clientSettings["notes"] !== client.notes ||
              client.clientSettings["phone"] !== client.phone ||
              client.clientSettings["is_competitor"] !== client.isCompetitor ||
              client.clientSettings["gender"] !== client.gender ||
              client.clientSettings["profile_squat"] !== client.profileSquat ||
              client.clientSettings["profile_bench_press"] !==
                client.profileBenchPress ||
              client.clientSettings["profile_deadlift"] !==
                client.profileDeadlift
            ? isHovered
              ? 0.5
              : 1
            : 0.5,
        }}
        disabled={
          !(
            client.clientSettings["name_for_coach"] !== client.nameForCoach ||
            client.clientSettings["surname_for_coach"] !==
              client.surnameForCoach ||
            client.clientSettings["notes"] !== client.notes ||
            client.clientSettings["phone"] !== client.phone ||
            client.clientSettings["is_competitor"] !== client.isCompetitor ||
            client.clientSettings["gender"] !== client.gender ||
            client.clientSettings["profile_squat"] !== client.profileSquat ||
            client.clientSettings["profile_bench_press"] !==
              client.profileBenchPress ||
            client.clientSettings["profile_deadlift"] !== client.profileDeadlift
          )
        }
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Text style={[Fonts.medium14, { color: Colors.whiteColor }]}>
          {t("save-string")}
        </Text>
      </TouchableOpacity>
    );
  });

  const DeleteButton = () => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <TouchableOpacity
        onPress={() => {
          if (!client.isOffline) {
            setShowingDeleteAlert(true);
          }
        }}
        style={{
          alignItems: "center",
          justifyContent: "space-around",
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Text
          style={[
            Fonts.medium14,
            {
              color: "rgb(240, 68, 56)",
              opacity: client.isOffline ? 0.5 : 1,
              fontWeight: client.isOffline
                ? "normal"
                : isHovered
                ? "600"
                : "normal",
            },
          ]}
        >
          {t("deleteClient-string")}
        </Text>
      </TouchableOpacity>
    );
  };

  const Separator = () => {
    return (
      <View
        style={{
          borderColor: "rgba(0,0,0,0.2)",
          opacity: 0.5,
          // flex: 1,
          width: "100%",
          borderRadius: 14,
          borderWidth: 0.1,
          marginBottom: 20,
        }}
      />
    );
  };

  const DeleteSampleClientButton = () => {
    const [isHovered, setIsHovered] = useState(false);
    const isSampleClient =
      client.email && client.email.includes("efortcoach.com");

    if (!isSampleClient) return null;

    return (
      <View style={{ marginBottom: 12, alignSelf: "flex-end" }}>
        <TouchableOpacity
          onPress={() => {
            if (!client.isOffline) {
              deleteClient();
            }
          }}
          style={{
            backgroundColor: "rgb(240, 68, 56)",
            borderRadius: 12,
            paddingVertical: 10,
            paddingHorizontal: 12,
            flexDirection: "row",
            alignItems: "center",
            opacity: client.isOffline ? 0.5 : isHovered ? 0.85 : 1,
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={client.isOffline}
        >
          <Text
            style={[
              Fonts.medium14,
              {
                color: Colors.whiteColor,
                fontSize: 13,
              },
            ]}
          >
            {t("deleteSampleAthlete-string")}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View
      style={[
        styles.container,
        { paddingTop: screenSize === "small" ? 16 : 24 },
      ]}
    >
      {client && (
        <>
          <DeleteSampleClientButton />
          <View
            style={{
              backgroundColor: Colors.whiteColor,
              padding: 20,
              borderRadius: 14,
              borderWidth: 0.1,
              borderColor: "rgba(0,0,0,0.2)",
            }}
          >
            <View
              style={[
                GlobalStyles.HStack,
                { alignItems: "center", paddingBottom: 20 },
              ]}
            >
              <Text
                style={[
                  Fonts.regular14,
                  { width: screenSize === "small" ? 120 : 180 },
                ]}
              >
                {t("clientName-string")}
              </Text>
              <View
                style={[
                  GlobalStyles.HStack,
                  {
                    flexDirection: "row",
                    flex: 1,
                  },
                ]}
              >
                <View
                  style={{
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "space-around",
                  }}
                >
                  <TextName
                    property={"nameForCoach"}
                    firestoreProperty={"name_for_coach"}
                    placeholder={"name-string"}
                  />
                  <View style={{ marginVertical: 4 }} />
                  <TextName
                    property={"surnameForCoach"}
                    firestoreProperty={"surname_for_coach"}
                    placeholder={"surname-string"}
                  />
                </View>
              </View>
            </View>
            <Separator />
            <View
              style={[
                GlobalStyles.HStack,
                { alignItems: "center", paddingBottom: 20 },
              ]}
            >
              <Text
                style={[
                  Fonts.regular14,
                  { width: screenSize === "small" ? 120 : 180 },
                ]}
              >
                {t("email-string")}
              </Text>
              <View
                style={[
                  GlobalStyles.HStack,
                  {
                    alignItems: "center",
                    justifyContent: "space-around",
                    flexDirection: "row",
                    flex: 1,
                  },
                ]}
              >
                <UnmutableTextName
                  property={"email"}
                  placeholder={"entrenador@gmail.com"}
                />
              </View>
            </View>
            <Separator />
            <View
              style={[
                GlobalStyles.HStack,
                { alignItems: "center", paddingBottom: 20 },
              ]}
            >
              <Text
                style={[
                  Fonts.regular14,
                  { width: screenSize === "small" ? 120 : 180 },
                ]}
              >
                {t("contactNumber-string")}
              </Text>
              <View
                style={[
                  GlobalStyles.HStack,
                  {
                    alignItems: "center",
                    justifyContent: "space-around",
                    flexDirection: "row",
                    flex: 1,
                  },
                ]}
              >
                <TextName
                  property={"phone"}
                  firestoreProperty={"phone"}
                  placeholder={"phone-string"}
                />
              </View>
            </View>
            <Separator />
            <View
              style={[
                GlobalStyles.HStack,
                { alignItems: "center", paddingBottom: 20 },
              ]}
            >
              <Text
                style={[
                  Fonts.regular14,
                  { width: screenSize === "small" ? 120 : 180 },
                ]}
              >
                {t("gender-string")}
              </Text>

              <View
                style={[
                  GlobalStyles.HStack,
                  {
                    alignItems: "center",
                    justifyContent: "space-around",
                    flexDirection: "row",
                    flex: 1,
                  },
                ]}
              >
                <SegmentedPicker
                  options={["female-string", "male-string"]}
                  value={client.clientSettings.gender}
                  onChange={(bool) => client.setSettings("gender", bool)}
                />
              </View>
            </View>
            <Separator />
            <View
              style={[
                GlobalStyles.HStack,
                { alignItems: "center", paddingBottom: 20 },
              ]}
            >
              <Text
                style={[
                  Fonts.regular14,
                  { width: screenSize === "small" ? 120 : 180 },
                ]}
              >
                {t("isCompetitor-string")}
              </Text>

              <View
                style={[
                  GlobalStyles.HStack,
                  {
                    alignItems: "center",
                    justifyContent: "space-around",
                    flexDirection: "row",
                    flex: 1,
                  },
                ]}
              >
                <SegmentedPicker
                  options={["yes-string", "No"]}
                  value={client.clientSettings.is_competitor}
                  onChange={(bool) => client.setSettings("is_competitor", bool)}
                />
              </View>
            </View>

            <BestCompetitionLifts />

            <Separator />
            <View
              style={[
                GlobalStyles.HStack,
                { alignItems: "center", flexGrow: 1 },
              ]}
            >
              <Text style={[Fonts.regular14, {}]}>{t("notes-string")}</Text>

              <TextNotes />
            </View>
          </View>
          <View
            style={{
              flexDirection: "column",
              alignItems: "flex-start",
              marginTop: screenSize === "small" ? 16 : 24,
              gap: 24,
            }}
          >
            <SaveButton />
            <DeleteButton />
          </View>
          {showingDeleteAlert && (
            <CustomAlert
              isVisible={showingDeleteAlert}
              onClose={() => setShowingDeleteAlert(false)}
              title={deleteAlertTitle}
              text={deleteAlertText}
              action={deleteClient}
              specialCTA={"delete-string"}
            />
          )}
        </>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  textInputStyle: {
    height: 40,
  },
  textInputContainer: {
    borderRadius: 12,
    borderColor: "rgba(0,0,0,0.2)",
    borderWidth: 0.1,
    padding: 10,
    height: 40,
  },

  competitionTextBoxContainer: {
    borderRadius: 12,
    borderWidth: 0.1,
    justifyContent: "center",
    alignItems: "center",
  },
  competitionTextBoxInput: {
    width: "100%",
    textAlign: "center",
    color: "black",
    paddingVertical: 6,
    outlineStyle: "none",
  },
  bestLiftsContainer: {
    alignItems: "center",
    paddingBottom: 20,
    justifyContent: "space-between",
    width: "100%",
  },
  liftRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  liftLabel: {},
  liftTextBoxWrapper: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
  },
});

export default ClientProfile;
